import React, { useState } from 'react';
import { Drawer, Button } from 'components/Elements';
import { Trash2 } from 'lucide-react';
import Skeleton from 'react-loading-skeleton';
import { useNotificationStore } from 'stores/notifications';
import { useUserSessions } from '../api/getUserSessions';
import { useRevokeUserSession } from '../api/revokeUserSession';
import { useRevokeAllSessions } from '../api/revokeAllSessions';
import { formatDate } from 'utils/format';
import { capitalize } from 'lodash';
import { motion, AnimatePresence } from 'framer-motion';
import { Smartphone, Dot } from 'lucide-react';
import { MapPin } from 'lucide-react';
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
  ZoomableGroup,
} from 'react-simple-maps';

const geoUrl = 'https://unpkg.com/world-atlas@2.0.2/countries-110m.json';

const SessionItem = ({ session, onRevoke, isRevoking, onViewDetails }) => {
  const {
    id,
    uniqueId,
    expiresAt,
    currentSession,
    location = 'Kampala',
    ip = '196.250.67.216',
    countryCode = 'UG',
    seenOn = 'Jul 21, 2025',
  } = session;

  const isExpired = new Date(expiresAt) < new Date();

  return (
    <div className="flex gap-3 items-start bg-white/10 rounded-3xl mb-3 p-3">
      <Smartphone className="text-white/40 w-5 h-5 mt-1" />

      <div className="flex-1">
        <p className="text-white text-sm font-medium">
          {location} {ip}
        </p>
        <div className="flex items-center text-xs text-white/50 gap-2 mt-1">
          <Dot
            className={`w-3 h-3 ${
              currentSession ? 'text-green-400' : 'text-gray-400'
            }`}
          />
          <span>
            {currentSession ? 'Current session' : `Last accessed on ${seenOn}`}
          </span>
        </div>
        <p className="text-xs text-white/50 mt-1">Seen in {countryCode}</p>
        <div className="mt-2 flex gap-2">
          <button
            disabled={isRevoking}
            onClick={() => onRevoke(id)}
            className="text-red-400 hover:underline hover:font-bold text-xs"
          >
            Revoke
          </button>
          <button
            onClick={() => onViewDetails(session)}
            className="text-white hover:underline hover:font-bold text-xs"
          >
            View Details
          </button>
        </div>
      </div>
    </div>
  );
};

const SessionList = ({ sessions, onRevoke, isRevoking, onViewDetails }) => {
  if (!sessions?.length) {
    return <p className="text-white/70 text-sm">No active sessions found.</p>;
  }

  return (
    <div className="flex flex-col">
      {sessions.map((session) => (
        <SessionItem
          key={session.id}
          session={session}
          onRevoke={onRevoke}
          isRevoking={isRevoking}
          onViewDetails={onViewDetails}
        />
      ))}
    </div>
  );
};

const SessionSkeleton = () => (
  <Skeleton
    height={100}
    borderRadius={12}
    baseColor="#2C2C2C"
    highlightColor="#3C3C3C"
  />
);

export const SessionDetailsPanel = ({ session, onClose }) => {
  if (!session) return null;

  const {
    location = 'Kampala, Central Region, Uganda',
    countryCode = 'UG',
    ip = '196.250.67.216',
    device = 'Chrome on macOS',
    seenOn = 'July 24, 2025',
    lat = 0.3612,
    lng = 32.5458,
  } = session;

  return (
    <motion.div
      initial={{ opacity: 0, x: 30 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 30 }}
      transition={{ duration: 0.3 }}
      className="w-3/4 bg-white/10 backdrop-blur-xl border border-white/10 shadow rounded-3xl p-6 text-white space-y-4"
    >
      <div className="flex justify-between items-center mb-2">
        <h3 className="text-lg font-semibold">Session Details</h3>
        <button
          onClick={onClose}
          className="text-sm text-white/60 hover:text-white"
        >
          Close
        </button>
      </div>

      <p className="text-sm text-white/70">Seen in {countryCode}</p>
      <p className="text-sm">
        <strong>Device:</strong> {device}
      </p>
      <p className="text-sm">
        <strong>Last location:</strong> {location}
      </p>
      <p className="text-sm">
        <strong>Signed in:</strong> {seenOn}
        <br />
        {location}
      </p>

      <div className="h-64 w-full rounded-xl overflow-hidden border border-white/20">
        <ComposableMap
          projectionConfig={{ scale: 150 }}
          width={800}
          height={400}
          className="w-full h-full"
        >
          <ZoomableGroup center={[lng, lat]} zoom={4}>
            <Geographies geography={geoUrl}>
              {({ geographies }) =>
                geographies.map((geo) => (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill="#1e293b"
                    stroke="#334155"
                    style={{
                      default: { outline: 'none' },
                      hover: { fill: '#475569', outline: 'none' },
                      pressed: { outline: 'none' },
                    }}
                  />
                ))
              }
            </Geographies>
            <Marker coordinates={[lng, lat]}>
              <MapPin color="#4ADE80" size={20} />
            </Marker>
          </ZoomableGroup>
        </ComposableMap>
      </div>
    </motion.div>
  );
};

export const SessionsDrawer = ({ userName, isOpen, onClose, userId }) => {
  const { addNotification } = useNotificationStore();
  const { data, isLoading, refetch } = useUserSessions({ userId });
  const [selectedSession, setSelectedSession] = useState(null);

  const { mutate: revokeSession, isLoading: isRevokingOne } =
    useRevokeUserSession({
      onSuccess: () => {
        refetch();
        addNotification({ type: 'success', title: 'Session revoked' });
      },
    });

  const { mutate: revokeAll, isLoading: isRevokingAll } = useRevokeAllSessions({
    onSuccess: () => {
      refetch();
      addNotification({ type: 'success', title: 'All sessions revoked' });
    },
  });

  const handleRevoke = (id) => {
    revokeSession({ data: { id } });
  };

  const handleRevokeAll = () => {
    if (
      window.confirm(
        'Are you sure you want to revoke all sessions? This will log the user out from all devices.'
      )
    ) {
      revokeAll({ userId });
    }
  };

  const renderFooter = () =>
    data?.sessions?.length > 1 ? (
      <Button
        onClick={handleRevokeAll}
        isLoading={isRevokingAll}
        variant="danger"
        className="w-fit"
      >
        {isRevokingAll ? 'Revoking...' : 'Revoke All Sessions'}
      </Button>
    ) : null;

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title={`${userName || 'User'}'s Sessions`}
      size="full"
      renderFooter={renderFooter}
    >
      <div className="space-y-4">
        <div className="text-white mb-2">
          <h2 className="text-lg font-semibold">Mobile Sessions</h2>
          <p className="text-sm text-white/50">
            Devices logged into {userName || 'user'}'s account. Revoke any
            suspicious sessions.
          </p>
        </div>

        {isLoading ? (
          Array.from({ length: 3 }).map((_, idx) => (
            <SessionSkeleton key={idx} />
          ))
        ) : (
          <div className="flex items-start gap-4">
            <div
              className={`${
                selectedSession ? 'w-1/4' : 'w-full'
              } transition-all`}
            >
              <SessionList
                sessions={data?.sessions}
                onRevoke={handleRevoke}
                isRevoking={isRevokingOne}
                onViewDetails={setSelectedSession}
              />
            </div>

            <AnimatePresence>
              {selectedSession && (
                <SessionDetailsPanel
                  session={selectedSession}
                  onClose={() => setSelectedSession(null)}
                />
              )}
            </AnimatePresence>
          </div>
        )}
      </div>
    </Drawer>
  );
};
